import { useEffect, useRef, useState } from 'react';
import { Microphone, Stop } from '@phosphor-icons/react';
import { toast } from 'sonner';

const LANGS = [
  { code: 'ta-IN', label: 'தமிழ்' },
  { code: 'kn-IN', label: 'ಕನ್ನಡ' },
  { code: 'en-IN', label: 'English' },
];

const SR = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);

export const VoiceInput = ({ onText }) => {
  const [lang, setLang] = useState(localStorage.getItem('voice_lang') || 'ta-IN');
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  const stoppedRef = useRef(true);

  useEffect(() => () => { stoppedRef.current = true; recRef.current?.stop(); }, []);

  if (!SR) return null;

  const stop = () => {
    stoppedRef.current = true;
    recRef.current?.stop();
    setListening(false);
  };

  const start = () => {
    const rec = new SR();
    rec.lang = lang;
    rec.continuous = true;
    rec.interimResults = false;
    rec.onresult = (e) => {
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) onText(e.results[i][0].transcript.trim());
      }
    };
    rec.onerror = (e) => {
      if (e.error === 'not-allowed') toast.error('Microphone access denied');
      else if (e.error !== 'no-speech' && e.error !== 'aborted') toast.error(`Voice error: ${e.error}`);
      if (e.error === 'not-allowed') stop();
    };
    rec.onend = () => { if (!stoppedRef.current) rec.start(); else setListening(false); };
    recRef.current = rec;
    stoppedRef.current = false;
    rec.start();
    setListening(true);
  };

  return (
    <div className="flex items-center gap-2" data-testid="voice-input">
      <select
        data-testid="voice-lang-select"
        value={lang}
        disabled={listening}
        onChange={(e) => { setLang(e.target.value); localStorage.setItem('voice_lang', e.target.value); }}
        className="h-8 rounded-md border border-[#E2E8E4] bg-white px-2 text-xs"
      >
        {LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
      </select>
      <button
        type="button"
        data-testid="voice-mic-button"
        onClick={listening ? stop : start}
        title={listening ? 'Stop recording' : 'Speak to type'}
        className={`h-8 w-8 rounded-full flex items-center justify-center transition-colors ${
          listening ? 'bg-red-600 text-white animate-pulse' : 'bg-[#2C4C3B] text-white hover:bg-[#1f3729]'
        }`}
      >
        {listening ? <Stop size={16} weight="fill" /> : <Microphone size={16} weight="fill" />}
      </button>
      {listening && <span className="text-xs text-red-600" data-testid="voice-listening">Listening…</span>}
    </div>
  );
};
